// Deterministic PRNG-based word shuffling and selection system
// Ensures puzzles are randomized and not alphabetical

import { solutionWords } from '@/data/processedSolutions';

// Fixed seed for deterministic shuffling (hex for "WORM")
const WORMBLE_SEED = 0x574F524D;

// Export seed for verification display
export const WORMBLE_SEED_VALUE = WORMBLE_SEED;

// Mulberry32 PRNG - high-quality deterministic random number generator
function mulberry32(seed: number): () => number {
  let state = seed;
  return function() {
    state = (state + 0x6D2B79F5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Shuffle array deterministically using seeded PRNG
function seededShuffle<T>(array: T[], seed: number): T[] {
  const shuffled = [...array];
  const rng = mulberry32(seed);
  
  // Fisher-Yates shuffle with seeded RNG
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  return shuffled;
}

// Cached shuffled solutions
let cachedShuffledSolutions: string[] | null = null;

// Get shuffled solution words (cached for performance)
export function getShuffledSolutions(): string[] {
  if (!cachedShuffledSolutions) {
    cachedShuffledSolutions = seededShuffle(solutionWords, WORMBLE_SEED);
  }
  return cachedShuffledSolutions;
}

// Get word for date using shuffled order (NOT alphabetical!)
export function getWordForDateShuffled(date: Date): string {
  // Use UTC to avoid timezone/DST issues - extract just year/month/day
  const epochUTC = Date.UTC(2024, 0, 1);
  const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  
  const daysSinceEpoch = Math.floor((dateUTC - epochUTC) / (1000 * 60 * 60 * 24));
  
  const shuffledWords = getShuffledSolutions();
  const wordIndex = Math.abs(daysSinceEpoch) % shuffledWords.length;
  
  return shuffledWords[wordIndex].toUpperCase();
}

// Validate no repeats in the shuffled list
export function validateNoRepeats(): boolean {
  const shuffled = getShuffledSolutions();
  const uniqueSet = new Set(shuffled);
  return uniqueSet.size === shuffled.length;
}

// Get puzzle info for a date range (for testing/debugging)
export function getPuzzleSequence(startDate: Date, count: number): Array<{ date: string; word: string; index: number }> {
  const sequence: Array<{ date: string; word: string; index: number }> = [];
  
  for (let i = 0; i < count; i++) {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    
    const word = getWordForDateShuffled(date);
    sequence.push({
      date: date.toISOString().split('T')[0],
      word,
      index: i
    });
  }
  
  return sequence;
}

// Verify PRNG produces consistent results
export function verifyPRNGConsistency(): boolean {
  const rng1 = mulberry32(WORMBLE_SEED);
  const rng2 = mulberry32(WORMBLE_SEED);
  
  // Test 100 values for consistency
  for (let i = 0; i < 100; i++) {
    if (rng1() !== rng2()) {
      return false;
    }
  }
  
  return true;
}

// Simple hash for verification (not cryptographic, just for comparison)
function simpleHash(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

// Get verification info for a specific date
export function getWordVerificationInfo(date: Date): {
  word: string;
  seed: number;
  seedHex: string;
  epochDate: string;
  daysSinceEpoch: number;
  wordIndex: number;
  totalWords: number;
  shuffleHash: string; // Hash of first 50 words for cross-client verification
  prngConsistent: boolean;
} {
  // Use UTC to avoid timezone/DST issues - extract just year/month/day
  const epochUTC = Date.UTC(2024, 0, 1);
  const dateUTC = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
  const daysSinceEpoch = Math.floor((dateUTC - epochUTC) / (1000 * 60 * 60 * 24));
  const shuffledWords = getShuffledSolutions();
  const wordIndex = Math.abs(daysSinceEpoch) % shuffledWords.length;
  
  // Create verification hash from first 50 shuffled words
  const first50 = shuffledWords.slice(0, 50).join('');
  const shuffleHash = simpleHash(first50);
  
  return {
    word: shuffledWords[wordIndex].toUpperCase(),
    seed: WORMBLE_SEED,
    seedHex: '0x574F524D',
    epochDate: '2024-01-01',
    daysSinceEpoch,
    wordIndex,
    totalWords: shuffledWords.length,
    shuffleHash,
    prngConsistent: verifyPRNGConsistency()
  };
}
